// src/pages/CommercialRentProperties.jsx
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { PublicAPI } from '../api/public';
import { useAppStore } from '../store/useAppStore';
import { useCurrencyStore } from '../store/useCurrencyStore';
import FilterBar from '../components/FilterBar';
import ListingCard from '../components/ListingCard';

const Title = styled.h1`margin: 14px 0;`;
const Grid = styled.div`
  display: grid; gap: 14px;
  grid-template-columns: 1fr 1fr;
  @media (max-width: 860px){ grid-template-columns: 1fr; }
`;
const Pager = styled.div`
  display:flex; gap:10px; justify-content:center; margin:14px 0 28px;
`;

export default function CommercialRentProperties() {
  const { t } = useTranslation();
  const regionId = useAppStore(s => s.regionId);
  const currency = useCurrencyStore(s => s.currency);
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [items, setItems] = React.useState([]);
  const [page, setPage] = React.useState(1);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [params, setParams] = React.useState({ property_type: 'commercial_rent' });

  // Подхватываем фильтры из query-string
  React.useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const p = { property_type: 'commercial_rent' }; // базовый фильтр для коммерческой аренды
    
    if (sp.get('district_id')) p.district_id = Number(sp.get('district_id'));
    if (sp.get('bedrooms')) {
      const n = Number(sp.get('bedrooms'));
      if (!Number.isNaN(n)) {
        p.bedrooms = n; p.bedrooms_min = n; p.bedrooms_max = n;
      }
    }
    if (sp.get('price_min')) p.price_min = Number(sp.get('price_min'));
    if (sp.get('price_max')) p.price_max = Number(sp.get('price_max'));
    if (sp.get('area_min')) p.area_min = Number(sp.get('area_min'));
    if (sp.get('area_max')) p.area_max = Number(sp.get('area_max'));
    if (sp.get('page')) setPage(Number(sp.get('page')) || 1);
    
    setParams(p);
  }, []);

  React.useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setErr('');
      try {
        const requestParams = {
          ...(params || {}),
          page,
          page_size: 8,
          region_id: regionId || undefined,
          currency: currency || undefined,
        };
        
        // Добавляем логирование для отладки
        const data = await PublicAPI.properties(requestParams);
        
        // Логируем ответ
        console.log('🏢 CommercialRentProperties API ответ:', data);
        
        const list = Array.isArray(data?.properties) ? data.properties : [];
        const tot = data?.pagination?.total ?? 0;
        setItems(list);
        setTotal(tot);
      } catch (e) {
        console.error('🏢 CommercialRentProperties API ошибка:', e);
        if (!cancelled) {
          setErr(e.message || 'Error');
          setItems([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [params, page, regionId, currency]);

  const handleSearch = (p) => {
    const out = { property_type: 'commercial_rent', ...(p || {}) };
    setItems([]);
    setPage(1);
    setParams(out);
    
    // синхронизация с URL
    const usp = new URLSearchParams();
    Object.entries(out).forEach(([k,v]) => { 
      if (v !== '' && v != null && k !== 'property_type') usp.set(k, String(v)); 
    });
    const url = `${window.location.pathname}?${usp.toString()}`;
    window.history.replaceState(null, '', url);
  };

  const totalPages = Math.max(1, Math.ceil((total || 0) / 8));
  const goTo = (n) => {
    const newPage = Math.max(1, Math.min(totalPages, n));
    setPage(newPage);
    
    // Обновляем URL с новой страницей
    const usp = new URLSearchParams(window.location.search);
    if (newPage === 1) {
      usp.delete('page');
    } else {
      usp.set('page', String(newPage));
    }
    const url = `${window.location.pathname}?${usp.toString()}`;
    window.history.replaceState(null, '', url);
  };

  return (
    <main className="container" style={{ padding: '10px 0 20px' }}>
      <Title>🏢 Коммерческая аренда</Title>

      <FilterBar
        mode="commercial_rent"
        onSearch={handleSearch}
        initialParams={params}
      />

      {err && <div style={{ color: '#c62828', marginBottom: 10 }}>{err}</div>}
      {!loading && !items.length && <div style={{ opacity: .75 }}>Ничего не найдено</div>}

      <Grid>
        {items.map(it => (
          <ListingCard key={it.id ?? it.slug ?? Math.random()} item={it} />
        ))}
      </Grid>

      {totalPages > 1 && (
        <Pager>
          <button className="btn" onClick={()=>goTo(page-1)} disabled={page<=1}>‹ Назад</button>
          {Array.from({length: totalPages}, (_,i)=>i+1).map(n=>(
            <button key={n} className="btn" data-active={n===page} onClick={()=>goTo(n)}>{n}</button>
          ))}
          <button className="btn" onClick={()=>goTo(page+1)} disabled={page>=totalPages}>Вперёд ›</button>
        </Pager>
      )}
    </main>
  );
}